import {
  Box,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";

import DragandDrop from "./DragandDrop";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import AxiosInstance from "../Config/AxiosInstance";
import { ApiList } from "../Config/ApiList";
import toast from "react-hot-toast";
import LocationSearchField from "./LocationSearchField";
import ColorPicker from "./ColorPicker";

const validationSchema = Yup.object().shape({
  clubName: Yup.string().required("Club Name is required"),
  location: Yup.string().required("Location is required"),
  clubID: Yup.string().required("Club Id is required"),
  description: Yup.string().required("Description is required"),
  logo: Yup.mixed().required("Club logo is required"),
  awsRegion: Yup.string().required("AWS Region is required"),
  dbInstanceClass: Yup.string().required("DB Instance Class is required"),
  colorCode: Yup.string().required("Club color is required"),
});

const AddClubForm = ({ onClose, onSuccess, GetAllClubs }) => {
  const [latlng, setLatLng] = useState({
    lat: null,
    lng: null,
  });
  const [awsRegions, setAwsRegions] = useState([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [instanceTypes, setInstanceTypes] = useState([]);
  const [isLoadingInstanceTypes, setIsLoadingInstanceTypes] = useState(false);

  const updateLatLng = (latLngs) => {
    setLatLng({ ...latlng, lat: latLngs.lat, lng: latLngs.lng });
  };

  // Fetch AWS regions on component mount - COMMENTED OUT FOR STATIC REGION
  // const fetchAwsRegions = async () => {
  //   setIsLoadingRegions(true);
  //   try {
  //     const response = await AxiosInstance.get(`${ApiList.GET_AWS_REGIONS}?from=aws_regions`);
  //     if (response?.data?.s) {
  //       setAwsRegions(response.data?.r);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching AWS regions:", error);
  //     toast.error("Failed to load AWS regions");
  //   } finally {
  //     setIsLoadingRegions(false);
  //   }
  // };

  // Static AWS region setup
  const staticAwsRegion = {
    id: 1,
    code: "eu-west-2",
    name: "Europe (London)",
    location: "London"
  };

  // Fetch instance types from the new API - UPDATED FOR STATIC REGION
  // const fetchInstanceTypes = async (selectedRegion) => {
  //   setIsLoadingInstanceTypes(true);
  //   try {
  //     const response = await AxiosInstance.get(`${ApiList.GET_INSTANCE_TYPES}?region=${selectedRegion}`);
  //     if (response?.data?.s) {
  //       // Store the response array directly
  //       setInstanceTypes(response.data?.r);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching instance types:", error);
  //     toast.error("Failed to load instance types");
  //   } finally {
  //     setIsLoadingInstanceTypes(false);
  //   }
  // };

  // Static fetch instance types for eu-west-2 - COMMENTED OUT FOR STATIC INSTANCE TYPE
  // const fetchInstanceTypes = async () => {
  //   setIsLoadingInstanceTypes(true);
  //   try {
  //     const response = await AxiosInstance.get(`${ApiList.GET_INSTANCE_TYPES}?region=eu-west-2`);
  //     if (response?.data?.s) {
  //       // Store the response array directly
  //       setInstanceTypes(response.data?.r);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching instance types:", error);
  //     toast.error("Failed to load instance types");
  //   } finally {
  //     setIsLoadingInstanceTypes(false);
  //   }
  // };

  // Static DB instance type setup
  const staticInstanceType = "db.t4g.small";

  useEffect(() => {
    // fetchAwsRegions(); // COMMENTED OUT FOR STATIC REGION
    // Set static AWS regions
    setAwsRegions([staticAwsRegion]);
    // Fetch instance types for the static region - COMMENTED OUT FOR STATIC INSTANCE TYPE
    // fetchInstanceTypes();
    // Set static instance types
    setInstanceTypes([staticInstanceType]);
  }, []);
  return (
    <Box sx={{ width: "100%" }}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "600",
            fontStyle: "italic",
            flex: 1,
            textAlign: "center",
          }}
        >
          ADD NEW CLUB
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Formik
          initialValues={{
            clubName: "",
            email: "",
            phone: "",
            countryCode: "",
            location: "",
            description: "",
            logo: null,
            clubID: "",
            awsRegion: "eu-west-2", // Static AWS region
            dbInstanceClass: "db.t4g.small", // Static DB instance class
            colorCode: "#000000",
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              const formData = new FormData();
              formData.append("club_name", values.clubName);
              formData.append("location", values.location);
              formData.append("description", values.description);
              formData.append("club_code", values.clubID);
              formData.append("logo", values.logo);
              formData.append("lat", latlng?.lat);
              formData.append("lng", latlng?.lng);
              formData.append("region", values.awsRegion);
              formData.append("db_instance_class", values.dbInstanceClass);
              formData.append("color_code", values.colorCode);

              const response = await AxiosInstance.post(
                ApiList?.ADD_CLUB,
                formData
              );
              if (response.data?.s === 1) {
                resetForm();
                GetAllClubs()
                toast?.success("Club added successfully.");
                onSuccess(response); // Call this instead of onClose()
              } else {
                toast.error(response?.data?.m);
              }
            } catch (error) {
              console.log(error);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting, // Add this
          }) => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography sx={{ fontWeight: "500", ml: 0.5, mb: 0.5 }}>
                      Upload Image
                    </Typography>
                    <DragandDrop
                      setImage={(files) => setFieldValue("logo", files[0])}
                      currentImages={values.logo ? [values.logo] : []}
                      sx={{}}
                    />
                    <Typography
                      sx={{
                        ml: 0.5,
                        fontSize: "12px",
                        color: "#d32f2f",
                        mb: 2,
                      }}
                    >
                      {" "}
                      {touched.logo && errors.logo}
                    </Typography>
                    <Box>
                      <Typography sx={{ fontWeight: "500", ml: 0.5, mb: 0.5 }}>
                        Club Name
                      </Typography>
                      <TextField
                        fullWidth
                        name="clubName"
                        placeholder="Club Name"
                        value={values.clubName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          mb: 0.5,

                          "& .MuiOutlinedInput-notchedOutline ": {
                            outline: "none",
                            border: "none",
                          },
                          borderRadius: "20px",
                          border: "none",
                          outline: "none",
                          background: "#f3f3f3",
                        }}
                      />
                      <Typography
                        sx={{
                          ml: 0.5,
                          fontSize: "12px",
                          color: "#d32f2f",
                          mb: 2,
                        }}
                      >
                        {" "}
                        {touched.clubName && errors.clubName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "500", ml: 0.5, mb: 0.5 }}>
                        Club ID
                      </Typography>
                      <TextField
                        fullWidth
                        name="clubID"
                        placeholder="Club ID"
                        value={values.clubID}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          mb: 0.5,

                          "& .MuiOutlinedInput-notchedOutline ": {
                            outline: "none",
                            border: "none",
                          },
                          borderRadius: "20px",
                          border: "none",
                          outline: "none",
                          background: "#f3f3f3",
                        }}
                      />
                      <Typography
                        sx={{
                          ml: 0.5,
                          fontSize: "12px",
                          color: "#d32f2f",
                          mb: 2,
                        }}
                      >
                        {" "}
                        {touched.clubID && errors.clubID}
                      </Typography>
                    </Box>

                    {/* Color Picker */}
                    <Box>
                      <ColorPicker
                        label="Club Color"
                        value={values.colorCode}
                        onChange={(color) => setFieldValue("colorCode", color)}
                        error={errors.colorCode}
                        touched={touched.colorCode}
                      />
                    </Box>
                  </Box>
                </Grid>
                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography sx={{ fontWeight: "500", ml: 0.5, mb: 0.5 }}>
                      Location
                    </Typography>
                    <LocationSearchField
                      address={values?.location} // Use Formik's value instead of separate state
                      handleAddress={(value) => {
                        setFieldValue("location", value); // Update Formik's value
                      }}
                      updateLatLng={updateLatLng}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      width={"100%"}
                      name="location"
                      error={errors.location}
                      touched={touched.location}
                    />
                    <Typography
                      sx={{
                        ml: 0.5,
                        fontSize: "12px",
                        color: "#d32f2f",
                        mb: 2,
                      }}
                    >
                      {" "}
                      {touched.location && errors.location}
                    </Typography>

                    <Box>
                      <Typography sx={{ fontWeight: "500", ml: 0.5, mb: 0.5 }}>
                        Description
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        name="description"
                        value={values.description}
                        onChange={(e) => {
                          if (e.target.value.length <= 300) {
                            handleChange(e);
                          }
                        }}
                        onBlur={handleBlur}
                        placeholder="Description (max 300 characters)"
                        inputProps={{ maxLength: 300 }}
                        helperText={`${values.description.length}/300 characters`}
                        sx={{
                          mb: 0.5,
                          "& .MuiOutlinedInput-notchedOutline ": {
                            outline: "none",
                            border: "none",
                          },
                          borderRadius: "20px",
                          border: "none",
                          outline: "none",
                          background: "#f3f3f3",
                        }}
                      />
                      <Typography
                        sx={{
                          ml: 0.5,
                          fontSize: "12px",
                          color: "#d32f2f",
                          mb: 2,
                        }}
                      >
                        {" "}
                        {touched.description && errors.description}
                      </Typography>
                    </Box>

                    {/* AWS Region Dropdown */}
                    <Box sx={{ display: "none" }}>
                      <Typography sx={{ fontWeight: "500", ml: 0.5, mb: 0.5 }}>
                        AWS Region
                      </Typography>
                      <FormControl
                        fullWidth
                        sx={{
                          mb: 0.5,
                          "& .MuiOutlinedInput-notchedOutline": {
                            outline: "none",
                            border: "none",
                          },
                          borderRadius: "20px",
                          background: "#f3f3f3",
                        }}
                      >
                        <Select
                          name="awsRegion"
                          value={values.awsRegion}
                          onChange={(e) => {
                            // COMMENTED OUT FOR STATIC REGION
                            // handleChange(e);
                            // Reset DB instance class when region changes
                            // setFieldValue("dbInstanceClass", "");
                            // Fetch instance types for the selected region
                            // if (e.target.value) {
                            //   fetchInstanceTypes(e.target.value);
                            // } else {
                            //   setInstanceTypes([]);
                            // }
                          }}
                          onBlur={handleBlur}
                          displayEmpty
                          disabled={true} // Always disabled for static region
                          sx={{
                            borderRadius: "20px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          }}
                        >
                          {awsRegions.map((region) => (
                            <MenuItem key={region.id} value={region.code}>
                              {region.name} ({region.code}) - {region.location}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography
                        sx={{
                          ml: 0.5,
                          fontSize: "12px",
                          color: "#d32f2f",
                          mb: 2,
                        }}
                      >
                        {touched.awsRegion && errors.awsRegion}
                      </Typography>
                    </Box>

                    {/* DB Instance Class Dropdown */}
                    <Box sx={{ display: "none" }}>
                      <Typography sx={{ fontWeight: "500", ml: 0.5, mb: 0.5 }}>
                        DB Instance Class
                      </Typography>
                      <FormControl
                        fullWidth
                        sx={{
                          mb: 0.5,
                          "& .MuiOutlinedInput-notchedOutline": {
                            outline: "none",
                            border: "none",
                          },
                          borderRadius: "20px",
                          background: "#f3f3f3",
                        }}
                      >
                        <Select
                          name="dbInstanceClass"
                          value={values.dbInstanceClass}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          displayEmpty
                          disabled={true} // Always disabled for static instance type
                          sx={{
                            borderRadius: "20px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          }}
                        >
                          {instanceTypes?.map((instanceClass) => (
                            <MenuItem key={instanceClass} value={instanceClass}>
                              {instanceClass}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography
                        sx={{
                          ml: 0.5,
                          fontSize: "12px",
                          color: "#d32f2f",
                          mb: 1,
                        }}
                      >
                        {touched.dbInstanceClass && errors.dbInstanceClass}
                      </Typography>

                    </Box>
                  </Box>
                </Grid>
                {/* Buttons */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={onClose}
                      sx={{
                        bgcolor: "#E0E0E0",
                        color: "#333",
                        borderRadius: "25px",
                        px: 4,
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": { bgcolor: "#BDBDBD" },
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      variant="contained"
                      onClick={() => handleSubmit()}
                      disabled={isSubmitting}
                      sx={{
                        bgcolor: "#00D8A2",
                        color: "#000938",
                        borderRadius: "25px",
                        px: 4,
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": { bgcolor: "#00b98e" },
                        "&:disabled": { bgcolor: "#cccccc" },
                      }}
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Box>
  );
};

export default AddClubForm;
